// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data (docs/AYNI_DATA_INTAKE_PLAN.md, phase 1: single files,
 * private): the shapes, limits and bucket layout the XR server
 * (`functions/api/data/`), the delivery Worker (`workers/data-delivery`)
 * and the browser share. The intake server implements the same from
 * docs/AYNI_DATA_INTAKE_API.md. No DOM, no Worker globals.
 *
 * **Quotas (owner decision O5, 2026-10-06):** at the limit new uploads (and
 * a continuous source's new releases) are refused, and nothing is ever
 * deleted. Since 2026-10-18 (owner) the Situation Room plans include own
 * data (`OWN_DATA_GRANT_QUOTA`); Researcher and Lab get it as the
 * `xr-cap-own-data` add-on, with their own quotas below; Department and any
 * other plan through the same key, with `OWN_DATA_GRANT_QUOTA`.
 */

import type { XrEntitlements, XrPlanId } from './xrPlans'

/** Bytes in a gigabyte, as quotas count them (decimal, as storage is sold). */
export const GB = 1_000_000_000

export interface OwnDataQuota {
  /** Everything the tenant keeps: originals, releases and exact values. */
  storedBytes: number
  /** The largest single job (one original file). */
  jobBytes: number
  /** Continuous sources (phase 3; counted, not yet offered). */
  continuousSources: number
}

/** O5 (decided 2026-10-06): the quotas of the plans that include own data. */
export const OWN_DATA_QUOTAS: Readonly<Partial<Record<XrPlanId, OwnDataQuota>>> = {
  researcher: { storedBytes: 50 * GB, jobBytes: 50 * GB, continuousSources: 1 },
  lab: { storedBytes: 250 * GB, jobBytes: 50 * GB, continuousSources: 5 },
}

/** O5: what `xr-cap-own-data` gives a plan with no row above (a plan by quote). */
export const OWN_DATA_GRANT_QUOTA: OwnDataQuota = { storedBytes: 50 * GB, jobBytes: 50 * GB, continuousSources: 1 }

/** The quota an account's entitlements give, or null when own data is not in its plan. */
export function ownDataQuota(ent: Pick<XrEntitlements, 'plan' | 'ownData'>): OwnDataQuota | null {
  if (!ent.ownData || !ent.plan) return null
  return OWN_DATA_QUOTAS[ent.plan.id] ?? OWN_DATA_GRANT_QUOTA
}

/** Plaintext bytes per uploaded part (the plan: 64 MB parts). */
export const OWN_DATA_PART_BYTES = 64 * 1024 * 1024
/** Most parts one upload may have (50 GB is 746 parts of 64 MiB). */
export const OWN_DATA_MAX_PARTS = 1000
/** Jobs a tenant may have in flight at once (uploading to review). */
export const OWN_DATA_MAX_ACTIVE_JOBS = 3
/** How long presigned part addresses work (the plan: one hour). */
export const UPLOAD_URL_TTL_S = 60 * 60
/** An upload not completed by then is abandoned and its key destroyed. */
export const UPLOAD_ABANDON_MS = 24 * 60 * 60 * 1000
/** Days between a licence lapsing and its tenant's data being crypto-shredded (O2). */
export const LAPSE_SHRED_DAYS = 30
/** The second warning goes this many days before shredding (O2: two warnings). */
export const SECOND_WARNING_DAYS_BEFORE = 7
/** Custody manifests and the access log are kept this long, even after deletion. */
export const CUSTODY_RETENTION_YEARS = 10
/**
 * After a lapse, read-only access goes to a tenant's owner only when that
 * account was seen holding the licence at most this long before the lapse
 * (security review 2026-10-06, H1): someone who left the lab long before
 * gets nothing. Seven days covers a daily check that did not run for a
 * few days.
 */
export const LAPSED_HOLDER_GRACE_MS = 7 * 24 * 60 * 60 * 1000
/**
 * A deleted dataset stays `deleting` (refused everywhere, not counted) at
 * least this long, and is purged again before it is marked `deleted`, so
 * objects a pipeline was still writing when the key was destroyed are
 * caught (H2).
 */
export const PURGE_FINAL_AFTER_MS = 24 * 60 * 60 * 1000
/** How long a purge waits for the intake server to confirm it stopped writing before it finishes anyway (and says so in the log). */
export const INTAKE_ACK_WAIT_MS = 7 * 24 * 60 * 60 * 1000
/** Daily checkpoints are only ever opened for this many days back, so deleted ones cannot be recreated over an edited past (M5). */
export const CHECKPOINT_MAX_AGE_DAYS = 7

/** The delivery Worker's origin. */
export const DATA_DELIVERY_ORIGIN = 'https://data.pachamama-studios.stream'

// ------------------------------------------------------------- identifiers

export const TENANT_ID = /^tn[0-9a-f]{24}$/
export const DATASET_ID = /^ds[0-9a-f]{24}$/
export const JOB_ID = /^jb[0-9a-f]{24}$/
/** A share (phase 4), a continuous source (phase 3), a step-up challenge, a source alert. */
export const SHARE_ID = /^sh[0-9a-f]{24}$/
export const SOURCE_ID = /^sr[0-9a-f]{24}$/
export const STEPUP_ID = /^su[0-9a-f]{24}$/
export const ALERT_ID = /^al[0-9a-f]{24}$/
/** A passkey registered for step-up. */
export const PASSKEY_ID = /^pk[0-9a-f]{24}$/
/** Release ids are chosen by the intake server: lower-case letters, digits and hyphens. */
export const RELEASE_ID = /^r[0-9a-z][0-9a-z-]{2,62}$/

export type DataIdKind = 'tn' | 'ds' | 'jb' | 'sh' | 'sr' | 'su' | 'al' | 'pk' | 'pr'

/** A new random id of a kind: its prefix and 24 hex digits (96 bits). */
export function newDataId(kind: DataIdKind): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12))
  return kind + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

// ----------------------------------------------------------------- layout

/** Where a job's original lands, encrypted, in quarantine. */
export const incomingPrefix = (tenant: string, job: string): string => `incoming/${tenant}/${job}/`
export const incomingKey = (tenant: string, job: string): string => `${incomingPrefix(tenant, job)}original.ayd`
/** Everything the delivery Worker may serve for one dataset sits under this. */
export const datasetPrefix = (tenant: string, dataset: string): string => `t/${tenant}/d/${dataset}/`
export const latestKey = (tenant: string, dataset: string): string => `${datasetPrefix(tenant, dataset)}latest.json`
export const releasePrefix = (tenant: string, dataset: string, release: string): string => `${datasetPrefix(tenant, dataset)}r/${release}/`
export const releaseDescriptorKey = (tenant: string, dataset: string, release: string): string => `${releasePrefix(tenant, dataset, release)}release.json`
export const exactPrefix = (tenant: string, dataset: string, release: string): string => `${datasetPrefix(tenant, dataset)}exact/${release}.zarr/`
export const custodyPrefix = (tenant: string): string => `custody/${tenant}/`
export const custodyManifestKey = (tenant: string, dataset: string, release: string): string => `${custodyPrefix(tenant)}${dataset}/${release}.custody.json`

// ------------------------------------------------------------------- jobs

/**
 * A job's states, in order (docs/AYNI_DATA_INTAKE_API.md, "Job states").
 * `uploaded` waits for the intake server to take the job; the last three
 * are final.
 */
export const JOB_STATES = [
  'uploading', 'uploaded', 'checking', 'inspecting', 'awaiting_choices', 'processing', 'review',
  'published', 'failed', 'expired', 'cancelled',
] as const
export type JobState = (typeof JOB_STATES)[number]
export const FINAL_JOB_STATES: readonly JobState[] = ['published', 'failed', 'expired', 'cancelled']
/** The states in which the uploader (or the lab's owner) may still cancel an upload: the file was never opened. */
export const CANCELLABLE_JOB_STATES: readonly JobState[] = ['uploading', 'uploaded', 'checking']

/** Regridding methods the customer may choose (plan: Processing, step 4). */
export const REGRID_METHODS = ['nearest', 'bilinear', 'conservative'] as const
export type RegridMethod = (typeof REGRID_METHODS)[number]

/** What the customer chooses after the intake report (step 3). */
export interface JobChoices {
  variable: string
  /** ISO 8601 instants; absent: the whole time axis. */
  time?: { start: string; end: string }
  /** West, south, east, north in degrees; absent: the whole grid. */
  region?: [number, number, number, number]
  palette: string
  /** Value range; absent: the 0.1–99.9 percentiles (the plan's default). */
  range?: { min: number; max: number }
  regrid: RegridMethod
  /** The required accuracy, the tolerance gate: `quantile` of absolute errors within `abs` (data units). */
  tolerance?: { quantile: number; abs: number }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const SHORT_TEXT = /^[\p{L}\p{N}_.:\-/ ]{1,128}$/u

/** The customer's choices checked for shape, or null. `variables` are the ones the intake report listed. */
export function parseJobChoices(input: unknown, variables: readonly string[]): JobChoices | null {
  if (!isRecord(input)) return null
  const { variable, time, region, palette, range, regrid, tolerance } = input
  if (typeof variable !== 'string' || !variables.includes(variable)) return null
  if (typeof palette !== 'string' || !SHORT_TEXT.test(palette)) return null
  if (typeof regrid !== 'string' || !(REGRID_METHODS as readonly string[]).includes(regrid)) return null
  const out: JobChoices = { variable, palette, regrid: regrid as RegridMethod }
  if (time !== undefined) {
    if (!isRecord(time) || typeof time.start !== 'string' || typeof time.end !== 'string') return null
    const s = Date.parse(time.start)
    const e = Date.parse(time.end)
    if (!Number.isFinite(s) || !Number.isFinite(e) || e < s) return null
    out.time = { start: new Date(s).toISOString(), end: new Date(e).toISOString() }
  }
  if (region !== undefined) {
    if (!Array.isArray(region) || region.length !== 4 || !region.every(finite)) return null
    const [w, s, e, n] = region as number[]
    if (w < -180 || e > 360 || w >= e || s < -90 || n > 90 || s >= n) return null
    out.region = [w, s, e, n]
  }
  if (range !== undefined) {
    if (!isRecord(range) || !finite(range.min) || !finite(range.max) || range.min >= range.max) return null
    out.range = { min: range.min, max: range.max }
  }
  if (tolerance !== undefined) {
    if (!isRecord(tolerance) || !finite(tolerance.quantile) || !finite(tolerance.abs)) return null
    if (tolerance.quantile <= 0 || tolerance.quantile > 1 || tolerance.abs <= 0) return null
    out.tolerance = { quantile: tolerance.quantile, abs: tolerance.abs }
  }
  return out
}

/** How many parts a file of `bytes` is cut into. */
export const partCount = (bytes: number): number => Math.max(1, Math.ceil(bytes / OWN_DATA_PART_BYTES))

/** Plaintext bytes of part `n` (1-based) of a file of `bytes`. */
export const partPlainBytes = (bytes: number, n: number): number =>
  n < partCount(bytes) ? OWN_DATA_PART_BYTES : bytes - OWN_DATA_PART_BYTES * (partCount(bytes) - 1)

/** A file name as kept (shown to its owner only): no control characters, at most 255 characters. */
export function cleanFileName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  // eslint-disable-next-line no-control-regex
  const name = raw.replace(/[\u0000-\u001f\u007f​-‏‪-‮⁦-⁩]/g, '').trim()
  return name.length > 0 && name.length <= 255 ? name : null
}
